import React, { useCallback, useEffect, useState } from 'react'
import { Button, CircularProgress, Dialog, IconButton, TextField, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ChatBubbleOutline, Close, WhatsApp } from '@material-ui/icons'

import { whatsappUrl } from '../../store/constants/contacts'
import { settings } from '../../store/constants'
import { isMobileDevice } from '../../lib/device'
import { AnalyticsService } from '../../services/analytics'
import { chatWidgetToken } from '../../lib/chat-widget'
import { chatArm, entryRoute, rememberChoice, rememberedChoice } from '../../lib/chat-entry'
import { loadWebChat, webChatScript } from '../../lib/chat-launch'

import { styles } from './styles'

// Once per module: makeStyles called inside a render built a new JSS sheet,
// with new class names, on every render (booking screen, 11 Oct 2026).
const useStyles = makeStyles(styles)

// One dialog for the whole site, mounted once in app.js. Every WhatsApp link
// and the chat button register their intent here rather than carrying their
// own copy of the form.
let openDialog = null

// The website chat is on for this page: the arm, the widget token and its
// script (the layout writes all three only together).
export const webChatOn = () => chatArm() === 'web' && !!chatWidgetToken() && !!webChatScript()

const userToken = () => {
  try { return globalThis.localStorage.getItem('token') } catch (e) { return null }
}

// What the website chat's script reports back (client/packs/web-chat.js).
const chatEvents = {
  chat_handoff_whatsapp: () => AnalyticsService.trackChatHandoffWhatsapp(window.location.pathname),
  chat_message_sent: () => AnalyticsService.trackChatMessageSent()
}

// Load the website chat (first tap only: its script, then Chatwoot's), tell
// it who this is, open it. Resolves true when it opened. lib/chat-launch.js.
export const startWebChat = async () => {
  try {
    const chat = await loadWebChat()
    await chat.start({
      host: settings.host,
      userToken: userToken(),
      posthogId: AnalyticsService.posthogDistinctId(),
      track: name => chatEvents[name] && chatEvents[name]()
    })
    AnalyticsService.trackChatWidgetOpened(true)
    return true
  } catch (err) {
    AnalyticsService.trackChatWidgetOpened(false)
    return false
  }
}

// The chat did not load: WhatsApp, with a line saying so.
const chatFailed = text => {
  if (openDialog) openDialog(text, { step: 'whatsapp', chatError: true })
  else window.open(whatsappUrl(text), '_blank', 'noopener')
}

// Props for any "message us on WhatsApp" link. On a phone the wa.me href is
// left alone: it opens WhatsApp and nothing beats that. On a desktop the
// click opens this dialog's WhatsApp step: an Open WhatsApp button (wa.me,
// which opens WhatsApp Desktop when installed, else WhatsApp's own page or
// Web), and under it the number form, for anyone without WhatsApp on the
// computer. With the website chat on (CHAT_ENTRY=webchat) it also offers
// "Chat here instead". If the dialog is somehow not mounted the link still
// works as before.
export const whatsappChatProps = text => ({
  href: whatsappUrl(text),
  target: '_blank',
  rel: 'noopener noreferrer',
  onClick: e => {
    if (isMobileDevice() || !openDialog) return
    e.preventDefault()
    openDialog(text)
  }
})

// Props for the chat button (components/whatsapp-fab), by lib/chat-entry.js:
// the on-page chat on a desktop; "Chat here" or "WhatsApp" on a phone (then
// their last choice); WhatsApp only while CHAT_ENTRY=control.
export const chatEntryProps = text => ({
  href: whatsappUrl(text),
  target: '_blank',
  rel: 'noopener noreferrer',
  onClick: e => {
    const route = entryRoute({ arm: webChatOn() ? 'web' : 'wa', token: chatWidgetToken(), mobile: isMobileDevice(), choice: rememberedChoice() })
    if (route === 'wa.me') return
    e.preventDefault()
    if (route === 'web') {
      startWebChat().then(ok => { if (!ok) chatFailed(text) })
    } else if (openDialog) {
      openDialog(text, { step: route === 'choose' ? 'choose' : 'whatsapp' })
    } else {
      window.open(whatsappUrl(text), '_blank', 'noopener')
    }
  }
})

export const WhatsAppChatDialog = React.memo(() => {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const [phone, setPhone] = useState('')
  const [firstName, setFirstName] = useState('')
  const [website, setWebsite] = useState('') // honeypot, hidden; real users leave it blank
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(null)
  const [text, setText] = useState(undefined) // the clicked link's prefilled message
  // 'choose' (a phone, website chat on: "Chat here" or "WhatsApp"),
  // 'whatsapp' (Open WhatsApp + the number form).
  const [step, setStep] = useState('whatsapp')
  const [chatLoading, setChatLoading] = useState(false)
  const [chatError, setChatError] = useState(null)

  useEffect(() => {
    openDialog = (linkText, options = {}) => {
      // Back to the start every time it is opened. Asking again is harmless:
      // the server will not message the same number twice in a day, and it
      // is the only way to retry a send that did not go.
      setDone(null)
      setSending(false)
      setError(null)
      setChatError(options.chatError ? "The chat didn't load just now. WhatsApp works, Thanks" : null)
      setChatLoading(false)
      setText(linkText)
      const first = options.step === 'choose' ? 'choose' : 'whatsapp'
      setStep(first)
      setOpen(true)
      if (first === 'choose') AnalyticsService.trackChatChooserOpened()
      else AnalyticsService.trackWhatsappChatOpened()
    }
    return () => { openDialog = null }
  }, [])

  const handleClose = useCallback(() => {
    setOpen(false)
    setError(null)
  }, [])

  const chatHere = useCallback(async () => {
    rememberChoice('web')
    setChatLoading(true)
    setChatError(null)
    const ok = await startWebChat()
    setChatLoading(false)
    if (ok) {
      setOpen(false)
    } else {
      setChatError("The chat didn't load just now. WhatsApp works, Thanks")
      setStep('whatsapp')
    }
  }, [])

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault()
      setSending(true)
      setError(null)

      try {
        const res = await fetch(`${settings.host}/api/v2/chat_request`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone, first_name: firstName, website })
        })
        const data = await res.json().catch(() => ({}))

        if (!res.ok) {
          setError(data.message || 'Something went wrong, please try again, Thanks')
          setSending(false)
          return
        }

        const sent = data.success !== false
        AnalyticsService.trackWhatsappChatRequested(sent)
        setDone({ sent: sent, message: data.message })
      } catch (err) {
        setError('No connection, please try again, Thanks')
        setSending(false)
      }
    },
    [phone, firstName, website]
  )

  const chatHereButton = (label, testId) => (
    <Button
      className={classes.chatHere}
      variant="contained"
      onClick={chatHere}
      disabled={chatLoading}
      data-testid={testId}
      startIcon={chatLoading ? null : <ChatBubbleOutline />}>
      {chatLoading ? <CircularProgress size={22} color="inherit" /> : label}
    </Button>
  )

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth aria-labelledby="whatsapp-chat-title">
      <div className={classes.panel}>
        <IconButton className={classes.close} onClick={handleClose} aria-label="Close">
          <Close />
        </IconButton>

        {step === 'choose' && !done ? (
          <div data-testid="chat-chooser">
            <Typography className={classes.title} id="whatsapp-chat-title">
              Chat with us
            </Typography>
            <Typography className={classes.blurb}>
              Ask about prices, free slots or your booking. We reply in a minute.
            </Typography>
            <div className={classes.choices}>
              {chatHereButton('Chat here', 'chat-here')}
              <Button
                className={classes.whatsappChoice}
                variant="contained"
                href={whatsappUrl(text)}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<WhatsApp />}
                onClick={() => {
                  rememberChoice('wa')
                  AnalyticsService.trackWhatsappChatOpenApp()
                  handleClose()
                }}>
                WhatsApp
              </Button>
            </div>
          </div>
        ) : done ? (
          <>
            <WhatsApp className={classes.doneIcon} />
            <Typography className={classes.title} id="whatsapp-chat-title">
              {done.sent ? 'Check your phone' : 'Thanks'}
            </Typography>
            <Typography className={classes.blurb}>{done.message}</Typography>
            <Button className={classes.submit} variant="contained" onClick={handleClose}>
              Close
            </Button>
          </>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <Typography className={classes.title} id="whatsapp-chat-title">
              <WhatsApp className={classes.titleIcon} />
              Chat on WhatsApp
            </Typography>
            <Typography className={classes.blurb}>
              Open WhatsApp on this computer, or leave your mobile and we'll message you.
            </Typography>
            {chatError && <div className={classes.error}>{chatError}</div>}

            <Button
              className={classes.submit}
              variant="contained"
              href={whatsappUrl(text)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                AnalyticsService.trackWhatsappChatOpenApp()
                handleClose()
              }}>
              Open WhatsApp
            </Button>
            <div className={classes.divider}>Or we'll message you</div>

            {/* Honeypot: off-screen, aria-hidden, not tabbable. Bots fill every
                field, so a non-blank value is dropped server-side. */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={website}
              onChange={e => setWebsite(e.target.value)}
              style={{ position: 'absolute', left: '-5000px', width: '1px', height: '1px', opacity: 0 }}
            />

            <TextField
              className={classes.field}
              label="Your mobile"
              type="tel"
              variant="outlined"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              inputProps={{ maxLength: 20, autoComplete: 'tel' }}
            />
            <TextField
              className={classes.field}
              label="First name (optional)"
              variant="outlined"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              inputProps={{ maxLength: 30, autoComplete: 'given-name' }}
            />

            {error && <div className={classes.error}>{error}</div>}

            <Button
              className={classes.submitQuiet}
              variant="outlined"
              type="submit"
              disabled={sending || phone.replace(/\D/g, '').length < 10}>
              {sending ? <CircularProgress size={22} color="inherit" /> : 'Message me on WhatsApp'}
            </Button>
            <Typography className={classes.note}>
              Your number is only used to answer you.
            </Typography>
            {webChatOn() && !chatError && (
              <div className={classes.instead}>{chatHereButton('Chat here instead', 'chat-here-instead')}</div>
            )}
          </form>
        )}
      </div>
    </Dialog>
  )
})
